import React from 'react';
import { Form } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';

import * as Headless from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import { Select } from '@oysterjs/ui/select';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { AnimateIn } from '@oysterjs/ui/transition';

import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { type VisibilitySelectorValue } from '~/components/visibility/visibility-selector.utils';
import { UseCase, renameUploadedFiles } from './presets.utils';
import {
  PresetOptionCards,
  presetOptionNameLines,
  presetOptionUploads,
  usePresetOptionCards
} from './preset-option-cards';
import { PresetOptionalUploads } from './preset-optional-uploads';
import { acordDocumentOptions } from './acord-document-options';
import { AcordDocumentPicker } from './acord-document-picker';
import {
  CaraWorkspaceEffortLevel,
  CaraWorkspaceOutputFormat,
  CaraWorkspacePreset,
  CaraWorkspaceUseCase,
  DocumentType,
  CaraWorkspaceFileInput,
  CaraWorkspaceAmsContextInput,
  CaraWorkspaceAmsRefInput
} from '~/api/graphql';
import { Button } from '@oysterjs/ui/button';
import { DialogBody, DialogActions } from '@oysterjs/ui/dialog';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  FileInput,
  emptyFileInputChange,
  hasFileInputContent,
  mergeFileInputChanges,
  type FileInputChange
} from '~/components/file';
import { Input } from '@oysterjs/ui/input';
import { Combobox } from '@oysterjs/ui/combobox';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import {
  AnimatedUploadProgress,
  extractUploadFiles
} from '~/components/cara-input/animated-upload-progress';
import { type CopilotActionError } from '~/lib/copilot-action-error';
import { CopilotSubmitError } from '~/components/overage-limit-error';
import { CaraInputTemplatePicker, TemplatePickerDialogs } from '~/components/cara-input/cara-input';
import { CaraTemplateAttachmentKind, idsByKind } from '~/components/cara-input/cara-attachment';
import { useCaraAttachments } from '~/components/cara-input/use-cara-attachments';
import { CaraInputAttachments } from '~/components/cara-input/cara-input-attachments';
import { resolveEffortLevel, useEffortLevelChoices } from './effort-level';
import { EffortLevelSelect } from './effort-level-select';
import { useTemplateLimit } from './template-limit';

type WorkspaceCreateVisibilityPicker = {
  value: VisibilitySelectorValue;
  onChange: (next: VisibilitySelectorValue) => void;
};

const WorkspaceCreateVisibilityContext =
  React.createContext<WorkspaceCreateVisibilityPicker | null>(null);

export const WorkspaceCreateVisibilityProvider = (props: {
  value: WorkspaceCreateVisibilityPicker | null;
  children: React.ReactNode;
}) => (
  <WorkspaceCreateVisibilityContext.Provider value={props.value}>
    {props.children}
  </WorkspaceCreateVisibilityContext.Provider>
);

type WorkspaceCreateEffortPicker = {
  value: CaraWorkspaceEffortLevel;
  onChange: (next: CaraWorkspaceEffortLevel) => void;
};

const WorkspaceCreateEffortContext = React.createContext<WorkspaceCreateEffortPicker | null>(null);

/** Null hides the effort selector, which is how the rollout gate turns it off. */
export const WorkspaceCreateEffortProvider = (props: {
  value: WorkspaceCreateEffortPicker | null;
  children: React.ReactNode;
}) => (
  <WorkspaceCreateEffortContext.Provider value={props.value}>
    {props.children}
  </WorkspaceCreateEffortContext.Provider>
);

export const WorkspaceCreateEffortSelect = (props: { disabled?: boolean }) => {
  const picker = React.useContext(WorkspaceCreateEffortContext);
  const { choices } = useEffortLevelChoices();
  if (!picker) {
    return null;
  }
  return (
    <EffortLevelSelect
      value={resolveEffortLevel(picker.value, choices)}
      onChange={picker.onChange}
      choices={choices}
      disabled={props.disabled}
    />
  );
};

export type CopilotCreateWorkspaceHandler = (
  query: string,
  files: CaraWorkspaceFileInput[],
  options?: CopilotWorkspaceCreateOptions
) => Promise<void>;

export type CopilotWorkspaceCreateOptions = {
  customFormIds?: string[];
  documentIds?: DocumentType[];
  htmlDocumentTemplateIds?: string[];
  presetOverride?: CaraWorkspacePreset;
  visibility?: VisibilitySelectorValue;
  wordDocumentTemplateIds?: string[];
  excelSpreadsheetTemplateIds?: string[];
  omitPresetOutputTemplates?: boolean;
  presetMetadata?: Record<string, unknown>;
  outputFormat?: CaraWorkspaceOutputFormat;
  effortLevel?: CaraWorkspaceEffortLevel;
  /**
   * The use case the caller launched. Carried as a typed field rather than a
   * `Use Case:` line in the message body, which is what the backend used to have
   * to recover with a regex.
   */
  useCase?: CaraWorkspaceUseCase;
  /**
   * Client to file the new workspace under. Sent on the create input, where
   * `@authorizeInput` checks CLIENT UPDATE on that client.
   */
  clientId?: string;
  amsContext?: CaraWorkspaceAmsContextInput[];
  /** The AMS browser's picks, sent by their AMS identity. */
  amsRefs?: CaraWorkspaceAmsRefInput[];
};

const amsContextCreateOption = (
  ...parts: FileInputChange[]
): Pick<CopilotWorkspaceCreateOptions, 'amsContext' | 'amsRefs'> => {
  const { amsContext, amsRefs = [] } = mergeFileInputChanges(...parts);
  return {
    ...(amsContext.length > 0 ? { amsContext } : {}),
    ...(amsRefs.length > 0 ? { amsRefs } : {})
  };
};

export type CopilotFormBaseProps = {
  onSubmit: CopilotCreateWorkspaceHandler;
  error?: CopilotActionError;
  onClose: () => void;
  outputTemplatesField?: React.ReactNode;
};

const WorkspaceCreateSubmitButton = (props: {
  loading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}) => {
  const visibility = React.useContext(WorkspaceCreateVisibilityContext);
  const effortSelect = <WorkspaceCreateEffortSelect disabled={props.loading} />;
  if (!visibility) {
    return (
      <>
        {effortSelect}
        <Button type="submit" color="sky" loading={props.loading} disabled={props.disabled}>
          {props.children ?? 'Create Workspace'}
        </Button>
      </>
    );
  }

  return (
    <>
      {effortSelect}
      <VisibilitySplitButton
        type="submit"
        value={visibility.value}
        onChange={visibility.onChange}
        loading={props.loading}
        disabled={props.disabled}
      >
        {props.children ?? 'Create Workspace'}
      </VisibilitySplitButton>
    </>
  );
};

export const CopilotCertificateReviewForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [certificateFiles, setCertificateFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [quoteFiles, setQuoteFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        message,
        [
          ...renameUploadedFiles(certificateFiles.files, '[COI/EOI] '),
          ...renameUploadedFiles(quoteFiles.files, '[Quote or Policy] ')
        ],
        amsContextCreateOption(certificateFiles, quoteFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Certificate of Insurance</Label>
              <Description>
                Upload the certificate of insurance or evidence of insurance you want Cara to
                review.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setCertificateFiles(change)}
              />
            </Field>
            <Field>
              <Label>Quotes or Policies</Label>
              <Description>
                Upload any quotes or policies you want Cara to compare to the certificate of
                insurance.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setQuoteFiles(change)}
              />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes you want Cara to consider when reviewing the certificate of
                insurance.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(certificateFiles, quoteFiles).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotContractReviewForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [contractFiles, setContractFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [quoteFiles, setQuoteFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        message,
        [
          ...renameUploadedFiles(contractFiles.files, '[Contract] '),
          ...renameUploadedFiles(quoteFiles.files, '[Quote or Policy] ')
        ],
        amsContextCreateOption(contractFiles, quoteFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Vendor Contract</Label>
              <Description>
                Upload the vendor contract you want Cara to review for insurance requirements.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setContractFiles(change)}
              />
            </Field>
            <Field>
              <Label>Quotes or Policies</Label>
              <Description>
                Upload any quotes or policies you want Cara to compare to the contract requirements.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setQuoteFiles(change)}
              />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes you want Cara to consider when reviewing the vendor
                contract.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(mergeFileInputChanges(contractFiles, quoteFiles).files)}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotCoverageComparisonForm = (
  props: CopilotFormBaseProps & {
    /** Agency HTML templates selectable as the output format; hides the option when empty. */
    htmlTemplateOptions?: Array<{ id: string; title: string }>;
    /** Agency Word templates selectable as the output format; hides the option when empty. */
    wordTemplateOptions?: Array<{ id: string; title: string }>;
  }
) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [compareOptionFiles, setCompareOptionFiles] = React.useState<FileInputChange[]>([
    emptyFileInputChange(),
    emptyFileInputChange()
  ]);
  const [outputFormat, setOutputFormat] = React.useState<CaraWorkspaceOutputFormat>(
    CaraWorkspaceOutputFormat.Text
  );
  const [htmlTemplateId, setHtmlTemplateId] = React.useState('');
  const [wordTemplateId, setWordTemplateId] = React.useState('');

  const htmlTemplateOptions = props.htmlTemplateOptions ?? [];
  const wordTemplateOptions = props.wordTemplateOptions ?? [];

  const needsHtmlTemplate = outputFormat === CaraWorkspaceOutputFormat.HtmlTemplate;
  const needsWordTemplate = outputFormat === CaraWorkspaceOutputFormat.WordTemplate;
  const missingTemplateSelection =
    (needsHtmlTemplate && !htmlTemplateId) || (needsWordTemplate && !wordTemplateId);

  const filesForUploadProgress = compareOptionFiles.flatMap((change) => change.files);

  const buildFilesForSubmit = (): CaraWorkspaceFileInput[] =>
    compareOptionFiles.flatMap((change, index) =>
      renameUploadedFiles(change.files, `[Option ${index + 1}] `)
    );

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(message, buildFilesForSubmit(), {
        htmlDocumentTemplateIds: needsHtmlTemplate && htmlTemplateId ? [htmlTemplateId] : undefined,
        wordDocumentTemplateIds: needsWordTemplate && wordTemplateId ? [wordTemplateId] : undefined,
        outputFormat: outputFormat === CaraWorkspaceOutputFormat.Text ? undefined : outputFormat,
        ...amsContextCreateOption(...compareOptionFiles)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            {compareOptionFiles.map((_, index) => (
              <Field key={index}>
                <div className="flex items-center justify-between gap-2" data-slot="label">
                  <Label>Option {index + 1}</Label>
                  {compareOptionFiles.length > 1 && (
                    <Headless.Button
                      as="button"
                      type="button"
                      aria-label={`Remove option ${index + 1}`}
                      onClick={() =>
                        setCompareOptionFiles((prev) => prev.filter((_, i) => i !== index))
                      }
                      className="shrink-0 p-0.5 rounded text-neutral-500 hover:text-red-600 hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <XMarkIcon className="size-4" />
                    </Headless.Button>
                  )}
                </div>
                <Description>Upload quote(s) or policies for this option to compare.</Description>
                <FileInput
                  multiple
                  enableExisting
                  enableAMSSearch
                  onChange={(change) =>
                    setCompareOptionFiles((prev) =>
                      prev.map((existing, i) => (i === index ? change : existing))
                    )
                  }
                />
              </Field>
            ))}
            <div>
              <Button
                type="button"
                color="white"
                onClick={() => setCompareOptionFiles((prev) => [...prev, emptyFileInputChange()])}
              >
                <PlusIcon className="size-4" />
                Add option
              </Button>
            </div>
            <Field>
              <Label>Output Format</Label>
              <Description>
                Choose how Cara should deliver the comparison. Text shows the analysis directly in
                the workspace.
              </Description>
              <RadioGroup
                value={outputFormat}
                onChange={(value) => setOutputFormat(value as CaraWorkspaceOutputFormat)}
              >
                <RadioField>
                  <Radio value={CaraWorkspaceOutputFormat.Text} color="sky" />
                  <Label>Text</Label>
                </RadioField>
                <RadioField>
                  <Radio value={CaraWorkspaceOutputFormat.Xlsx} color="sky" />
                  <Label>Excel (XLSX)</Label>
                </RadioField>
                {htmlTemplateOptions.length > 0 && (
                  <RadioField>
                    <Radio value={CaraWorkspaceOutputFormat.HtmlTemplate} color="sky" />
                    <Label>HTML template</Label>
                  </RadioField>
                )}
                {wordTemplateOptions.length > 0 && (
                  <RadioField>
                    <Radio value={CaraWorkspaceOutputFormat.WordTemplate} color="sky" />
                    <Label>Word template</Label>
                  </RadioField>
                )}
              </RadioGroup>
            </Field>
            {needsHtmlTemplate && (
              <Field>
                <Label>HTML Template</Label>
                <Description>
                  Cara populates the selected template with the comparison results.
                </Description>
                <Select
                  value={htmlTemplateId}
                  onChange={(e) => setHtmlTemplateId(e.currentTarget.value)}
                >
                  <option value="">Select a template…</option>
                  {htmlTemplateOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {needsWordTemplate && (
              <Field>
                <Label>Word Template</Label>
                <Description>
                  Cara populates the selected template with the comparison results.
                </Description>
                <Select
                  value={wordTemplateId}
                  onChange={(e) => setWordTemplateId(e.currentTarget.value)}
                >
                  <option value="">Select a template…</option>
                  {wordTemplateOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes you want Cara to consider when comparing the quotes or
                policies.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(filesForUploadProgress)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} disabled={missingTemplateSelection} />
      </DialogActions>
    </Form>
  );
};

export const CopilotGenerateTemplateForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [documentIds, setDocumentIds] = React.useState<DocumentType[]>([]);
  const [certificateHolder, setCertificateHolder] = React.useState('');
  const [additionalInterest, setAdditionalInterest] = React.useState('');

  const templateLimit = useTemplateLimit();

  const onSubmit = async () => {
    setLoading(true);
    try {
      let finalMessage = '';
      if (message.trim()) {
        finalMessage += `\n\n${message.trim()}\n\n`;
      }
      if (
        documentIds.includes(DocumentType.Acord_25) ||
        documentIds.includes(DocumentType.Acord_24) ||
        documentIds.includes(DocumentType.Acord_30)
      ) {
        finalMessage += `**Certificate Holder**\n\n${certificateHolder.trim() || "Use the named insured's information as the certificate holder."}\n\n`;
      }
      if (
        documentIds.includes(DocumentType.Acord_27) ||
        documentIds.includes(DocumentType.Acord_28)
      ) {
        finalMessage += `**Additional Interest**\n\n${additionalInterest.trim() || 'Reference the documentation accordingly.'}\n\n`;
      }
      await props.onSubmit(finalMessage.trim(), renameUploadedFiles(inputFiles.files, '[Input] '), {
        documentIds,
        ...amsContextCreateOption(inputFiles)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Forms, certificates, and ID cards</Label>
              <Description>
                Choose the forms, certificates, and ID cards you&apos;d like Cara to fill out. You
                can select up to {templateLimit.count} ACORD forms at once. To fill additional
                forms, create another workspace or wait for this one to complete.
              </Description>
              <AcordDocumentPicker
                options={acordDocumentOptions}
                value={documentIds}
                onChange={setDocumentIds}
                maxSelections={templateLimit.count}
                placeholder="Search templates"
              />
            </Field>
            <Field>
              <Label>Input Files</Label>
              <Description>
                Any files you'd like Cara to parse and use as input to cross-file into templates.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setInputFiles(change)}
              />
            </Field>
            {(documentIds.includes(DocumentType.Acord_24) ||
              documentIds.includes(DocumentType.Acord_25) ||
              documentIds.includes(DocumentType.Acord_30)) && (
              <Field>
                <Label>Certificate Holder</Label>
                <Description>
                  Name and address of the certificate holder (if any). If not provided, Cara will
                  use the named insured's information as the certificate holder.
                </Description>
                <Textarea
                  value={certificateHolder}
                  onChange={(e) => setCertificateHolder(e.currentTarget.value)}
                  rows={3}
                />
              </Field>
            )}
            {(documentIds.includes(DocumentType.Acord_27) ||
              documentIds.includes(DocumentType.Acord_28)) && (
              <Field>
                <Label>Additional Interest</Label>
                <Description>
                  Name and address of the additional interest (if any). If not provided, Cara will
                  reference the documentation accordingly.
                </Description>
                <Textarea
                  value={additionalInterest}
                  onChange={(e) => setAdditionalInterest(e.currentTarget.value)}
                  rows={3}
                />
              </Field>
            )}
            <Field>
              <Label>Additional Information</Label>
              <Description>
                Add any additional information you want Cara to consider when filling out the
                template. This can be specific instructions or other free-form information that
                should be parsed and mapped to template fields
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={5}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton disabled={!documentIds.length} loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotDocumentExplorationForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('ask_policy_question');

  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  const isExploreExclusions = useCase === 'explore_exclusions';
  const isSummarizeCoverage = useCase === 'summarize_coverage';

  const fileDesc = isExploreExclusions
    ? 'Upload the policy you want Cara to explore for exclusions.'
    : isSummarizeCoverage
      ? 'Upload the policy or quote you want Cara to summarize coverage for.'
      : 'Upload the policy or document you want Cara to use as context to answer questions.';
  const textareaLabel = isExploreExclusions
    ? 'Areas to Explore (Optional)'
    : isSummarizeCoverage
      ? 'Additional Instructions (Optional)'
      : 'Question or Prompt';
  const textareaDesc = isExploreExclusions
    ? 'Specify a particular question or scenario you want Cara to evaluate each exclusion against, or leave blank for a complete exclusions analysis.'
    : isSummarizeCoverage
      ? 'Specify any particular lines of coverage or sections you want Cara to focus on, or leave blank for a full summary.'
      : "Any question or prompt you'd like Cara to answer about the documents.";

  const onSubmit = async () => {
    setLoading(true);
    try {
      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      const fullMessage = [message, '', `Use Case: ${useCase}`].join('\n');
      await props.onSubmit(
        fullMessage.trim(),
        renameUploadedFiles(inputFiles.files, '[Policy] '),
        amsContextCreateOption(inputFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Use Case</Label>
              <Description>Choose how you want Cara to explore this document.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'ask_policy_question') as UseCase)}
                options={[
                  {
                    value: 'ask_policy_question',
                    label: 'Ask a Policy Question',
                    description:
                      'Ask Cara a specific question about the policy, quote, or other document.'
                  },
                  {
                    value: 'explore_exclusions',
                    label: 'Explore Exclusions',
                    description:
                      'Get a comprehensive analysis of exclusions across all forms, sorted by relevance, with optional context on a specific agent question.'
                  },
                  {
                    value: 'summarize_coverage',
                    label: 'Summarize Coverage',
                    description:
                      'Get a detailed summary of all coverage limits, sublimits, deductibles, and retentions across all policy documents.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>Policy or Document</Label>
              <Description>{fileDesc}</Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setInputFiles(change)}
              />
            </Field>
            <Field>
              <Label>{textareaLabel}</Label>
              <Description>{textareaDesc}</Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={5}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotGenerateQuoteProposalForm = (
  props: Omit<CopilotFormBaseProps, 'outputTemplatesField'> & {
    outputTemplatesField: React.ReactNode;
  }
) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [proposalOptionFiles, setProposalOptionFiles] = React.useState<FileInputChange[]>([
    emptyFileInputChange()
  ]);
  const [currentPolicyFiles, setCurrentPolicyFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [renewalOptionFiles, setRenewalOptionFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  const [documentType, setDocumentType] = React.useState<string>('other');

  React.useEffect(() => {
    setProposalOptionFiles([emptyFileInputChange()]);
    setCurrentPolicyFiles(emptyFileInputChange());
    setRenewalOptionFiles(emptyFileInputChange());
    setInputFiles(emptyFileInputChange());
  }, [documentType]);

  const filesForUploadProgress =
    documentType === 'quote'
      ? proposalOptionFiles.flatMap((change) => change.files)
      : documentType === 'renewal'
        ? mergeFileInputChanges(currentPolicyFiles, renewalOptionFiles).files
        : inputFiles.files;

  const buildFilesForSubmit = (): CaraWorkspaceFileInput[] => {
    if (documentType === 'quote') {
      return proposalOptionFiles.flatMap((change, index) =>
        renameUploadedFiles(change.files, `[Option ${index + 1}] `)
      );
    }
    if (documentType === 'renewal') {
      return [
        ...renameUploadedFiles(currentPolicyFiles.files, '[Current Policy] '),
        ...renameUploadedFiles(renewalOptionFiles.files, '[Proposed Option] ')
      ];
    }
    return renameUploadedFiles(inputFiles.files, '[Input Document] ');
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(message, buildFilesForSubmit(), {
        presetMetadata: {
          generateQuoteProposalDocumentType: documentType
        },
        ...amsContextCreateOption(
          ...(documentType === 'quote'
            ? proposalOptionFiles
            : documentType === 'renewal'
              ? [currentPolicyFiles, renewalOptionFiles]
              : [inputFiles])
        )
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Document Type</Label>
              <Description>
                Choose the type of document you want Cara to generate. This guides Cara's analysis
                and output format.
              </Description>
              <Combobox
                value={documentType}
                onChange={(v) => v && setDocumentType(v)}
                options={[
                  {
                    value: 'quote',
                    label: 'Quote Proposal',
                    description:
                      'A competitive coverage proposal presenting policy options, premiums, and terms for a prospective or existing client.'
                  },
                  {
                    value: 'renewal',
                    label: 'Renewal Proposal',
                    description:
                      'A renewal proposal summarizing changes to an expiring policy, updated pricing, and recommended coverage adjustments for the upcoming term.'
                  },
                  {
                    value: 'summary',
                    label: 'Summary or Notice',
                    description:
                      'A plain-language overview containing a summarization of key information or a notice to the client.'
                  },
                  {
                    value: '',
                    label: 'Other',
                    description:
                      'Any other document you want Cara to generate. The instructions below will be used to guide the document generation.'
                  }
                ]}
              />
            </Field>
            {documentType === 'quote' && (
              <>
                {proposalOptionFiles.map((_, index) => (
                  <Field key={index}>
                    <div className="flex items-center justify-between gap-2" data-slot="label">
                      <Label>Option {index + 1}</Label>
                      {proposalOptionFiles.length > 1 && (
                        <Headless.Button
                          as="button"
                          type="button"
                          aria-label={`Remove option ${index + 1}`}
                          onClick={() =>
                            setProposalOptionFiles((prev) => prev.filter((_, i) => i !== index))
                          }
                          className="shrink-0 p-0.5 rounded text-neutral-500 hover:text-red-600 hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <XMarkIcon className="size-4" />
                        </Headless.Button>
                      )}
                    </div>
                    <Description>
                      Upload any quotes or binders you want Cara to use as part of this option to
                      present in the proposal.
                    </Description>
                    <FileInput
                      multiple
                      enableExisting
                      enableAMSSearch
                      onChange={(change) =>
                        setProposalOptionFiles((prev) =>
                          prev.map((existing, i) => (i === index ? change : existing))
                        )
                      }
                    />
                  </Field>
                ))}
                <div>
                  <Button
                    type="button"
                    color="white"
                    onClick={() =>
                      setProposalOptionFiles((prev) => [...prev, emptyFileInputChange()])
                    }
                  >
                    <PlusIcon className="size-4" />
                    Add option
                  </Button>
                </div>
              </>
            )}
            {documentType === 'renewal' && (
              <>
                <Field>
                  <Label>Current Policy</Label>
                  <Description>
                    Upload the current policy for reference in the renewal proposal.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => setCurrentPolicyFiles(change)}
                  />
                </Field>
                <Field>
                  <Label>Proposed Option</Label>
                  <Description>
                    Upload the renewal quote or policy to present in the renewal proposal.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    enableAMSSearch
                    onChange={(change) => setRenewalOptionFiles(change)}
                  />
                </Field>
              </>
            )}
            {(documentType === 'summary' || documentType === 'other') && (
              <Field>
                <Label>Input Documents</Label>
                <Description>
                  Upload any documents Cara should use when generating the document.
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  enableAMSSearch
                  onChange={(change) => setInputFiles(change)}
                />
              </Field>
            )}
            <Field>
              <Label>Generation Instructions</Label>
              <Description>
                Additional instructions and context to provide to Cara when generating the document.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(filesForUploadProgress)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotUnderwritingAnalysisForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE THESE WITHOUT CHANGING THE BACKEND
  const validLinesOfBusiness = [
    {
      value: 'Business Operations',
      displayValue: 'Business Operations (including Liability Coverage)'
    },
    { value: 'Workers Compensation', displayValue: 'Workers Compensation' },
    { value: 'Property', displayValue: 'Property Coverage' },
    { value: 'Auto', displayValue: 'Auto Coverage' }
  ];

  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [linesOfBusiness, setLinesOfBusiness] = React.useState<string[]>(['Business Operations']);

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
        `${message}\n\nCoverage Information: ${linesOfBusiness.join(', ')}`,
        renameUploadedFiles(inputFiles.files, '[Input Document] '),
        amsContextCreateOption(inputFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Application Documents</Label>
              <Description>
                Upload the commercial insurance application and any supporting documents for Cara to
                review.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setInputFiles(change)}
              />
            </Field>
            <Field>
              <Label>Coverage Review</Label>
              <Description>
                Select the lines of business you would like reviewed for this application.
              </Description>
              <CheckboxGroup>
                {validLinesOfBusiness.map(({ value, displayValue }) => (
                  <CheckboxField>
                    <Checkbox
                      color="sky"
                      checked={linesOfBusiness.includes(value)}
                      disabled={value === 'Business Operations'}
                      onChange={(v) =>
                        setLinesOfBusiness((prev) =>
                          v ? [...prev, value] : prev.filter((p) => p !== value)
                        )
                      }
                    />
                    <Label>{displayValue}</Label>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes or specific requirements you want Cara to consider during
                the analysis. You can also input a list of addresses for Cara to research.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotSOVAnalysisForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('sov_documents_and_web');
  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [outputExcel, setOutputExcel] = React.useState(false);

  const webSearchEnabled = useCase === 'sov_documents_and_web' || useCase === 'sov_address_only';
  const showInputDocuments = useCase !== 'sov_address_only';
  const notesSectionTitle =
    useCase === 'sov_document_only'
      ? 'Additional Information'
      : 'Research Addresses and Additional Information';
  const notesSectionDescription =
    useCase === 'sov_document_only'
      ? 'Add any additional notes or specific requirements you want Cara to consider during the analysis.'
      : useCase === 'sov_address_only'
        ? 'Paste or type the list of addresses to research (one per line or in any format). Cara will research these and create a Statement of Values from public data.'
        : 'Add specific addresses to research and any additional notes you want Cara to consider.';

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage =
        useCase === 'sov_address_only'
          ? 'Research the following addresses and create a Statement of Values.'
          : 'Create a Statement of Values and COPE analysis from the provided documents.';
      const userMessage = message.trim() || defaultMessage;

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      const messageLines = [
        userMessage,
        '',
        `Web Search and Verification: ${webSearchEnabled ? 'enabled' : 'disabled'}`,
        `Output Format: ${outputExcel ? 'Excel' : 'text'}`,
        `Use Case: ${useCase}`
      ];
      const fullMessage = messageLines.join('\n');

      const filesToSubmit =
        useCase === 'sov_address_only'
          ? []
          : renameUploadedFiles(inputFiles.files, '[Input Document] ');

      await props.onSubmit(
        fullMessage,
        filesToSubmit,
        useCase === 'sov_address_only' ? undefined : amsContextCreateOption(inputFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose how you want to run this SOV and COPE analysis.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'sov_documents_and_web') as UseCase)}
                options={[
                  {
                    value: 'sov_document_only',
                    label: 'Document aggregation only',
                    description:
                      'You have documents (e.g. ACORD forms, prior applications). Cara aggregates them and builds one SOV.'
                  },
                  {
                    value: 'sov_documents_and_web',
                    label: 'Documents + web verification',
                    description:
                      'Same as above, plus Cara uses public data to verify and enrich (tenant roles, claims, risk info).'
                  },
                  {
                    value: 'sov_address_only',
                    label: 'Address-only research',
                    description:
                      'Provide addresses only. Cara researches them from public data and builds an SOV from scratch.'
                  }
                ]}
              />
            </Field>

            {showInputDocuments && (
              <Field>
                <Label>Input Documents</Label>
                <Description>
                  Upload any documents you want Cara to use for the Statement of Values. Addresses
                  will be extracted from the provided documents, or you can specify addresses in the
                  section below.
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  enableAMSSearch
                  onChange={(change) => setInputFiles(change)}
                />
              </Field>
            )}

            <Field>
              <Label>{notesSectionTitle}</Label>
              <Description>{notesSectionDescription}</Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={useCase === 'sov_address_only' ? 5 : 3}
              />
            </Field>

            <Field>
              <Label>Output Format</Label>
              <Description>
                By default, the output will be text-based tables and notes. Select Excel to generate
                an Excel file instead (takes longer to generate).
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={outputExcel}
                    onChange={(v) => setOutputExcel(!!v)}
                  />
                  <Label>Output as Excel file (takes longer)</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotClaimsReviewForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [claimFiles, setClaimFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [policyFiles, setPolicyFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [applicationFiles, setApplicationFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        message,
        [
          ...renameUploadedFiles(claimFiles.files, '[Claim Document] '),
          ...renameUploadedFiles(policyFiles.files, '[Policy Document] '),
          ...renameUploadedFiles(applicationFiles.files, '[Application Document] ')
        ],
        amsContextCreateOption(claimFiles, policyFiles, applicationFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Claim Documents</Label>
              <Description>
                Upload claim-related documents such as the first notice of loss, claim forms,
                incident reports, damage assessments, or police reports.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setClaimFiles(change)}
              />
            </Field>
            <Field>
              <Label>Policy Documents</Label>
              <Description>
                Upload the policy(ies) involved including declarations pages, policy forms, and
                endorsements.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setPolicyFiles(change)}
              />
            </Field>
            <Field>
              <Label>Application &amp; Supporting Documents (Optional)</Label>
              <Description>
                Upload the original application and any supporting documents for misrepresentation
                analysis.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setApplicationFiles(change)}
              />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional context about the claim, specific coverage questions, or areas of
                concern you want Cara to focus on.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(claimFiles, policyFiles, applicationFiles).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotLossRunAnalysisForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [lossRunFiles, setLossRunFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [supportingFiles, setSupportingFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        message,
        [
          ...renameUploadedFiles(lossRunFiles.files, '[Loss Run] '),
          ...renameUploadedFiles(supportingFiles.files, '[Supporting Document] ')
        ],
        amsContextCreateOption(lossRunFiles, supportingFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Loss Run Documents</Label>
              <Description>
                Upload all available loss runs. Include all years and lines of business. If no loss
                runs are provided, Cara will assume no losses.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setLossRunFiles(change)}
              />
            </Field>
            <Field>
              <Label>Policy, Application, or Supporting Documents</Label>
              <Description>
                Upload the application or any supporting documents to provide additional context
                about the insured&rsquo;s operations and risk controls.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setSupportingFiles(change)}
              />
            </Field>
            <Field>
              <Label>Additional Notes (Optional)</Label>
              <Description>
                Add any additional context about the insured, specific concerns, or focus areas for
                the risk mitigation statement.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(lossRunFiles, supportingFiles).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotCoverageGapAnalysisForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [policyFiles, setPolicyFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [additionalFiles, setAdditionalFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        message,
        [
          ...renameUploadedFiles(policyFiles.files, '[Policy Document] '),
          ...renameUploadedFiles(additionalFiles.files, '[Supporting Document] ')
        ],
        amsContextCreateOption(policyFiles, additionalFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Policy or Quote Documents (Recommended)</Label>
              <Description>
                Include all lines of business in the submission. Upload current policy(ies) or
                quote(s) including declarations pages, policy forms, and endorsements.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setPolicyFiles(change)}
              />
            </Field>
            <Field>
              <Label>Supporting Documents (Optional)</Label>
              <Description>
                Upload any applications, schedules, or other supporting documents.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setAdditionalFiles(change)}
              />
            </Field>
            <Field>
              <Label>Additional Notes (Optional)</Label>
              <Description>
                Add any context about the client&rsquo;s operations, known exposures, or specific
                concerns you want Cara to focus on.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(policyFiles, additionalFiles).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotRiskReportForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [outputPdf, setOutputPdf] = React.useState(true);
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  const onSubmit = async () => {
    setLoading(true);
    try {
      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND (workspace_messages.go parses Output Format)
      const query = [`Output Format: ${outputPdf ? 'PDF' : 'text'}`, message]
        .filter(Boolean)
        .join('\n\n');
      await props.onSubmit(
        query,
        renameUploadedFiles(inputFiles.files, '[Document] '),
        amsContextCreateOption(inputFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Policy and Other Documents</Label>
              <Description>
                Upload any documents describing the business (applications, policies, or operational
                summaries). Cara will use these alongside deep industry research.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(files) => setInputFiles(files)}
              />
            </Field>
            <Field>
              <Label>Business Name, Industry, or Description (Optional)</Label>
              <Description>
                Describe the client&apos;s business (name, type of operations, industry, and state).
                Example: &quot;ABC Roofing, commercial roofing contractor in Austin, TX&quot;.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
                placeholder="ABC Plumbing LLC, a residential and commercial plumbing contractor in Dallas, TX with 25 employees"
              />
            </Field>
            <Field>
              <Label>Output Format</Label>
              <Description>
                Generate a visually designed PDF report. Uncheck to receive plain text output
                instead (faster).
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox color="sky" checked={outputPdf} onChange={(v) => setOutputPdf(!!v)} />
                  <Label>Output as PDF report (takes longer)</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={!hasFileInputContent(inputFiles)}
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotRiskMatchingForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [publicDataVerification, setPublicDataVerification] = React.useState(true);

  const onSubmit = async () => {
    setLoading(true);
    try {
      const userMessage =
        message.trim() || 'Extract the risk details and identify market placement options.';

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      const messageLines = [
        userMessage,
        '',
        `Public Data Verification and Enrichment: ${publicDataVerification ? 'enabled' : 'disabled'}`
      ];
      const fullMessage = messageLines.join('\n');

      await props.onSubmit(
        fullMessage,
        renameUploadedFiles(inputFiles.files, '[Input Document] '),
        amsContextCreateOption(inputFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Input Documents</Label>
              <Description>
                Upload the application, financials, loss runs, or other documents for risk
                extraction.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setInputFiles(change)}
              />
            </Field>

            <Field>
              <Label>Additional Notes (Optional)</Label>
              <Description>
                Add any specific instructions, class codes, lines of business, states, or other
                context you want Cara to consider.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>

            <Field>
              <Label>Public Data Verification and Enrichment</Label>
              <Description>
                When enabled, Cara will search the web to verify and enrich the risk profile —
                confirming locations, reviewing the company website, and researching lawsuits, news,
                and OSHA violations for commercial risks. For personal risks with property, Cara
                will look up property details. When disabled, web search is limited to class code
                research only.
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={publicDataVerification}
                    onChange={(v) => setPublicDataVerification(!!v)}
                  />
                  <Label>Utilize public data verification and enrichment of the client</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBContributionAnalysisForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('create_analysis');
  const [message, setMessage] = React.useState('');
  const [censusFiles, setCensusFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [currentFiles, setCurrentFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [renewalFiles, setRenewalFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [alternateFiles, setAlternateFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [invoiceFiles, setInvoiceFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [payrollFrequency, setPayrollFrequency] = React.useState('derive');
  const [budgetTarget, setBudgetTarget] = React.useState('');
  // The workbook default follows the use case until the broker says otherwise,
  // and their choice then sticks across use-case switches. A worksheet is a
  // spreadsheet by nature — it goes onto a projector in front of the employer —
  // so defaulting it off there would make the common path a two-step.
  const [outputExcelChoice, setOutputExcelChoice] = React.useState(false);
  const [outputExcelTouched, setOutputExcelTouched] = React.useState(false);

  const isSolveToBudget = useCase === 'solve_to_budget';
  const isDeductionSchedule = useCase === 'build_deduction_schedule';
  // The worksheet run is the one that needs no current contribution structure:
  // it prices structures Cara proposes onto the quoted rates rather than
  // measuring a change against what the employer does today. Every other run is
  // delta-shaped and falls apart without that anchor.
  const isWorksheet = useCase === 'build_contribution_worksheet';

  // Each control is shown only where the run can act on it. A hidden control is
  // also excluded from the payload: a budget target left behind by a use-case
  // switch would silently turn the budget-solve stage back on, and a renewal
  // left behind would give the run a phantom column to price.
  const showBudgetTarget = isSolveToBudget;
  const showAlternateQuotes = !isDeductionSchedule;
  // Separating the incumbent's renewal from competing quotes is worth a field
  // only where the renewal is the do-nothing baseline every option has to beat,
  // which is the analysis runs. A worksheet prices structures onto quoted rates
  // for a group that may hold no coverage at all: there is no default to beat,
  // nothing downstream reads the distinction, and asking for it splits one pile
  // of carrier paper across two boxes for nothing.
  const showRenewal = !isWorksheet;
  // A deduction schedule computes off exactly one rate set — the one effective
  // for the plan year the deductions start in — and the expiring rates only buy
  // a change-from-today column. Two boxes named Current and Renewal invite the
  // error they exist to prevent: the rates about to go into effect are the ones
  // a broker calls current, so the set the schedule must compute from lands in
  // the box for the set it merely compares against, and every paycheck for a
  // year is wrong. One bucket takes both and intake sorts them by effective
  // date, which is printed on the documents and cannot be misfiled.
  const showCurrentRates = !isDeductionSchedule;
  // The invoice only feeds the baseline reconciliation, which ties last year's
  // census and rates to last year's bill. Deduction schedules and worksheets
  // price forward and never look at last year's bill. The budget solve does
  // look backward, but only to read current spend, which it already computes
  // from the rates, census, and structure it requires — the bill re-proves a
  // number rather than supplying one, so it does not earn a box of its own.
  const showInvoice = useCase === 'create_analysis';
  // Deductions are the one output denominated in paychecks, so that run asks
  // for the calendar. Everywhere else the calendar is either derivable from the
  // packet or not needed, and a worksheet built off nothing but carrier rates
  // usually has no calendar in the packet to find — which is the argument for
  // reporting it monthly, not for adding a control.
  const showPayrollFrequency = isDeductionSchedule;
  const outputExcel = outputExcelTouched ? outputExcelChoice : isWorksheet;

  const notesDesc = isDeductionSchedule
    ? 'The structure the employer selected, plus any class, location, or waiting-period rules payroll must honor.'
    : isWorksheet
      ? 'Contribution structures the employer has in mind, an employer HSA or HRA seed, class or location differences, and anything ruled out. Leave it blank for a standard set. Name the pay calendar here for per-pay deductions instead of monthly.'
      : 'Contribution philosophy, employer HSA or HRA seed amounts, class or location differences, and anything the employer has ruled out.';

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isSolveToBudget
        ? 'Solve for a contribution structure that meets the employer’s budget target.'
        : isDeductionSchedule
          ? 'Build the payroll deduction schedule for the selected contribution structure.'
          : isWorksheet
            ? 'Build the contribution worksheet from these rates, pricing a set of contribution structures on the census.'
            : 'Create a contribution analysis pricing the current plan and every alternative on the same census.';

      // Which box a document was filed under is the only reliable signal of what
      // it is: carriers label alternatives "renewal options" and brokers attach
      // a renewal as background, so reading intent out of the packet misreads
      // both. The backend branches the comparison on these two lines. The
      // deduction schedule declares neither — it reads the renewal for its rates
      // and has no column to compare it against.
      const comparesPlans = !isDeductionSchedule;
      const renewalSupplied = showRenewal && hasFileInputContent(renewalFiles);
      const optionsSupplied = showAlternateQuotes && hasFileInputContent(alternateFiles);

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      const messageLines = [
        message.trim() || defaultMessage,
        '',
        ...(showPayrollFrequency && payrollFrequency !== 'derive'
          ? [`Payroll Frequency: ${payrollFrequency}`]
          : []),
        ...(showBudgetTarget && budgetTarget.trim()
          ? [`Employer Budget Target: ${budgetTarget.trim()}`]
          : []),
        ...(comparesPlans
          ? [
              `Renewal Supplied: ${renewalSupplied ? 'yes' : 'no'}`,
              `Plan Options Supplied: ${optionsSupplied ? 'yes' : 'no'}`
            ]
          : []),
        `Output Format: ${outputExcel ? 'Excel' : 'text'}`,
        `Use Case: ${useCase}`
      ];

      await props.onSubmit(messageLines.join('\n'), [
        ...renameUploadedFiles(censusFiles.files, '[Census] '),
        ...(showCurrentRates ? renameUploadedFiles(currentFiles.files, '[Current Rates] ') : []),
        // The schedule's single rate bucket takes both plan years, so its label
        // cannot claim either one. `[Plan Rates]` says only "these are rates" and
        // leaves which-is-which to the effective dates, where the answer is.
        ...(showRenewal
          ? renameUploadedFiles(
              renewalFiles.files,
              isDeductionSchedule ? '[Plan Rates] ' : '[Renewal] '
            )
          : []),
        ...(showAlternateQuotes ? renameUploadedFiles(alternateFiles.files, '[Plan Option] ') : []),
        ...(showInvoice ? renameUploadedFiles(invoiceFiles.files, '[Invoice] ') : [])
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose what you want out of this renewal analysis.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'create_analysis') as UseCase)}
                options={[
                  {
                    value: 'create_analysis',
                    label: 'Analyze contributions at renewal',
                    description:
                      'Upload the census, the current plan with today’s contributions, and the renewal or any quoted options. Each option is priced on the same census and measured against today.'
                  },
                  {
                    value: 'build_contribution_worksheet',
                    label: 'Build contribution worksheet',
                    description:
                      'Turn carrier rates into the employer and employee split per plan and tier. A set of contribution structures is priced on your census, with no current structure required.'
                  },
                  {
                    value: 'solve_to_budget',
                    label: 'Solve to a budget',
                    description:
                      'Start from the employer’s target and get the structure that hits it, or what blocks it.'
                  },
                  {
                    value: 'build_deduction_schedule',
                    label: 'Build deduction schedule',
                    description:
                      'Turn a chosen structure into per-pay-period deductions by plan, tier, and class.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>Census and Enrollment</Label>
              <Description>
                Eligibility roster with each employee’s plan, tier, class, and waiver status. Add
                salary or hourly rate and hours to test ACA affordability per employee rather than
                the poverty-line safe harbor. CSV or Excel.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setCensusFiles(change)} />
            </Field>
            {showCurrentRates && (
              <Field>
                {/* No "(Optional)" tag on the worksheet even though the run builds
                    without this. The tag reads as "this may be ignored", which is
                    the opposite of true — supplied current rates are what every
                    change-from-today column is measured from. The condition it was
                    carrying is narrower than "optional" and belongs in the first
                    clause of the description, where a group with no coverage today
                    reads it and moves on. */}
                <Label>Current Plan and Rates</Label>
                <Description>
                  {isWorksheet
                    ? 'Expiring rates and what the employer contributes now, so each structure also shows the change from today. Leave empty if the group has no coverage yet.'
                    : 'The expiring rate sheet and plan design (summary of benefits or SBC), plus what the employer contributes by tier.'}
                </Description>
                <FileInput multiple enableExisting onChange={(change) => setCurrentFiles(change)} />
              </Field>
            )}
            {showRenewal && (
              <Field>
                {/* A renewal is one alternative among several — the one the incumbent
                    issued — so it shares a field set with quoted options rather than
                    a use case of its own. Both analysis runs drop the "(Optional)"
                    tag: one is named for the renewal and the other is nearly always
                    reached by way of one, so tagging the headline input optional
                    reads as a form disagreeing with the use case the broker just
                    picked. A run genuinely without a renewal is handled by the
                    description and by a submit button that never gates on it.
                    On the deduction schedule this same field holds every rate set,
                    under a name that does not invite misfiling. */}
                <Label>{isDeductionSchedule ? 'Plan Rates' : 'Renewal Plan and Rates'}</Label>
                <Description>
                  {isDeductionSchedule
                    ? 'Rates the deductions come off, for the plan year they start in. Add the expiring rates and today’s deductions here too for a change-from-current column; the two sets are told apart by effective date.'
                    : 'The incumbent’s renewal offer with plan design and rate guarantee. Separates the carrier’s rate action from your own enrollment change. Leave empty when shopping mid-year.'}
                </Description>
                <FileInput multiple enableExisting onChange={(change) => setRenewalFiles(change)} />
              </Field>
            )}
            {showAlternateQuotes && (
              <Field>
                {/* "Other" rather than a bare "Plan Options", so the boxes above
                    read as a sequence — what the group has now, what the incumbent
                    offered, everything else — instead of three unranked piles that
                    leave a broker wondering whether the current plan gets priced. */}
                <Label>
                  {isWorksheet ? 'Other Plans and Rates' : 'Other Plan Options (Optional)'}
                </Label>
                <Description>
                  {isWorksheet
                    ? 'Every other plan under consideration, with rates and plan design for each: the incumbent’s renewal, other carriers’ quotes, or both. All are priced on the same census as the current plan.'
                    : 'Competing carrier or plan quotes beyond the renewal, with the rates and plan design for each. Add as many as you want compared; all are priced on your census.'}
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  onChange={(change) => setAlternateFiles(change)}
                />
              </Field>
            )}
            {/* The sibling EB presets collect their optional uploads behind one
                disclosure. This form has nothing to put in one: every bucket
                above is what a run is built on somewhere — the current rates on
                the two analyses, the renewal on the deduction schedule, the
                quotes on the worksheet — and the invoice below is the only
                always-optional bucket left, on the one run that still shows it.
                Collapsing a single bucket trades a control for a control and
                charges a click for it. */}
            {showInvoice && (
              <Field>
                <Label>Current Carrier Invoice (Optional)</Label>
                <Description>
                  The most recent monthly bill. Reconciled against the census and current rates to
                  verify the baseline before modeling.
                </Description>
                <FileInput multiple enableExisting onChange={(change) => setInvoiceFiles(change)} />
              </Field>
            )}
            {showPayrollFrequency && (
              <Field>
                <Label>Benefit Deduction Periods</Label>
                <Description>
                  How many times a year the deduction comes out, not how many pay dates there are:
                  biweekly employers often deduct over 24. Leave it unset and the calendar comes
                  from the documents, or figures come back monthly.
                </Description>
                <Combobox
                  value={payrollFrequency}
                  minQueryLength={0}
                  maxOptions={10}
                  onChange={(v) => setPayrollFrequency(v || 'derive')}
                  options={[
                    { value: 'derive', label: 'Not sure (derive from the documents)' },
                    { value: '26', label: 'Biweekly (26 per year)' },
                    { value: '24', label: 'Semi-monthly, or biweekly skipping two (24 per year)' },
                    { value: '12', label: 'Monthly (12 per year)' },
                    { value: '52', label: 'Weekly (52 per year)' }
                  ]}
                />
              </Field>
            )}
            {showBudgetTarget && (
              <Field>
                <Label>Employer Budget Target (Required)</Label>
                <Description>
                  What the employer will accept: a percentage, a dollar figure, or &ldquo;hold our
                  spend flat&rdquo;. The solve returns the structure that hits it, or names what
                  blocks it.
                </Description>
                <Input
                  value={budgetTarget}
                  onChange={(e) => setBudgetTarget(e.currentTarget.value)}
                  placeholder="No more than a 4% increase in employer cost"
                />
              </Field>
            )}
            <Field>
              <Label>Additional Notes</Label>
              <Description>{notesDesc}</Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            <Field>
              <Label>Output Format</Label>
              <Description>
                {isSolveToBudget
                  ? 'The analysis comes back as text and tables. The workbook adds a solver tab where you can change the target in the meeting and watch the figures recalculate.'
                  : isDeductionSchedule
                    ? 'The analysis comes back as text and tables. Add a workbook when the deduction schedule is too large to read inline.'
                    : isWorksheet
                      ? 'The worksheet comes back as text and tables either way. The workbook adds one tab per contribution structure, formatted to present.'
                      : 'The analysis comes back as text and tables. The workbook is the employer’s copy: a cost breakdown by plan and tier that reads without narration.'}
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={outputExcel}
                    onChange={(v) => {
                      setOutputExcelTouched(true);
                      setOutputExcelChoice(!!v);
                    }}
                  />
                  <Label>Also build an Excel workbook (takes longer)</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(
                    censusFiles,
                    showCurrentRates ? currentFiles : emptyFileInputChange(),
                    showRenewal ? renewalFiles : emptyFileInputChange(),
                    showAlternateQuotes ? alternateFiles : emptyFileInputChange(),
                    showInvoice ? invoiceFiles : emptyFileInputChange()
                  ).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* Without a target there is nothing to solve for: the budget stage and the
            workbook's solver tab both key off the target line being present.
            The worksheet has the same shape of dependency on rates — it is a grid
            of rates split three ways, so with no rate sheet anywhere in the form
            there is nothing to split and the run would produce an empty table.
            The deduction schedule is the same dependency one bucket over: it
            computes off the merged Plan Rates file, and an empty bucket is a
            year of paychecks with nothing to deduct. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={
            (showBudgetTarget && !budgetTarget.trim()) ||
            (isWorksheet &&
              !hasFileInputContent(currentFiles) &&
              !hasFileInputContent(alternateFiles)) ||
            (isDeductionSchedule && !hasFileInputContent(renewalFiles))
          }
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBDocumentReviewForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('review_benefits_guide');
  const [message, setMessage] = React.useState('');
  const [subjectFiles, setSubjectFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [rateFiles, setRateFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  // One bucket for every source that is not a rate sheet. The contributions
  // worksheet and the plan documents used to have boxes of their own because
  // each declared itself to the prompt on a line that downgrades a family of
  // fields when it is empty — but the box could only ever report where the
  // broker filed a document, and an SBC dropped into "supporting documents"
  // produced a review announcing that no plan documents were supplied. Both
  // kinds are recognisable on their face, so the prompt now establishes which
  // it found by reading the packet and refuses to verify plan design or the
  // employer's split against anything else.
  const [supportFiles, setSupportFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [outputExcel, setOutputExcel] = React.useState(false);

  // On a contributions run the worksheet is the document under review, and plan
  // design is out of scope — nobody asking whether the rates are right wants the
  // deductibles checked against an SBC. The same three buckets serve both runs;
  // only their labels change.
  const isContributions = useCase === 'verify_contributions';

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isContributions
        ? 'Check the contributions worksheet against the carrier rate sheets.'
        : 'Review the benefits guide against the documents it was built from.';

      // No pay calendar and no supplied-source declarations. The backend still
      // parses `Payroll Frequency:`, `Contributions Supplied:`, and
      // `Plan Documents Supplied:` for composed API messages, but the form has
      // nothing honest to put on them: the calendar belongs in the notes as an
      // assertion the review checks the document against, and whether a plan
      // document is in the packet is something the run can only know by reading.
      const messageLines = [
        message.trim() || defaultMessage,
        '',
        `Output Format: ${outputExcel ? 'Excel' : 'text'}`,
        `Use Case: ${useCase}`
      ];

      // `[Support]` asserts nothing about what kind of document a file is, which
      // is the point: the bucket holds worksheets, SBCs, and renewal letters
      // alike, and 039 sorts them by what is printed inside.
      await props.onSubmit(messageLines.join('\n'), [
        ...renameUploadedFiles(subjectFiles.files, '[Under Review] '),
        ...renameUploadedFiles(rateFiles.files, '[Rate Sheet] '),
        ...renameUploadedFiles(supportFiles.files, '[Support] ')
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose which document you want checked.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'review_benefits_guide') as UseCase)}
                options={[
                  {
                    value: 'review_benefits_guide',
                    label: 'Review benefits guide',
                    description:
                      'Check an EBG or open enrollment packet against the rate sheets, plan documents, and contributions behind it.'
                  },
                  {
                    value: 'verify_contributions',
                    label: 'Verify contributions',
                    description:
                      'Check a contributions worksheet against the carrier rate sheets: rates, the split, and the deduction math.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>
                {isContributions ? 'Contributions Worksheet' : 'Employee Benefits Guide'}
              </Label>
              <Description>
                {isContributions
                  ? 'The worksheet being checked. Every other upload is a source it is checked against.'
                  : 'The document going to employees: a guide, open enrollment packet, or presentation. Every other upload is a source it is checked against.'}
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setSubjectFiles(change)} />
            </Field>
            <Field>
              <Label>Carrier Rate Sheets</Label>
              <Description>
                The rate exhibits the rates should match, for the plan year under review. Add one
                per carrier; each plan is checked against its own carrier’s file.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setRateFiles(change)} />
            </Field>
            <Field>
              {/* "Other" so the three boxes read as a sequence — the document,
                  the rates it should match, everything else it was built from —
                  rather than leaving a broker to wonder whether the rate sheet
                  also counts as a supporting document. The "(Optional)" tag
                  comes off on the guide run: without a worksheet and plan
                  documents every contribution and every deductible in the
                  review comes back unverified, which is not a run degrading
                  gracefully but the largest block of the register going
                  yellow. The tag stays where it is honest — the worksheet run
                  checks rates and arithmetic in full without this box. Neither
                  run gates submit on it. */}
              <Label>
                {isContributions
                  ? 'Other Supporting Documents (Optional)'
                  : 'Other Supporting Documents'}
              </Label>
              <Description>
                {isContributions
                  ? 'Anything else that bears on the split: a memo or renewal letter recording what the employer agreed to contribute, a payroll register or deduction report, or last year’s worksheet. Rates and arithmetic are checked in full without these.'
                  : 'Everything else the guide was built from: the contributions worksheet with the employer and employee split, the plan documents (SBCs, benefit summaries, or the schedule of benefits), a revised carrier kit, a renewal letter, or last year’s version. Without the worksheet, contributions are checked for arithmetic but not against what the employer decided; without plan documents, every deductible, copay, and out-of-pocket maximum comes back unverified.'}
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setSupportFiles(change)} />
            </Field>
            {/* No pay-frequency selector. The document declares its own pay
                periods and the prompt derives the calendar from the packet; a
                broker who knows the employer's payroll states it in the notes,
                where the prompt ranks it as the yardstick — the same authority
                the dropdown carried, without a control most users had to be
                told to leave alone. */}
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Narrow the review if you want only part of it: which benefit lines, which sections,
                or which documents to check against which. State the employer’s pay calendar here
                and every per-pay figure is checked against that rather than the document’s claim.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            <Field>
              <Label>Output Format</Label>
              <Description>
                The review comes back as text and tables. The workbook is the working copy: one row
                per finding with a status column to tick off as you edit.
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={outputExcel}
                    onChange={(v) => setOutputExcel(!!v)}
                  />
                  <Label>Also build an Excel workbook (takes longer)</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(subjectFiles, rateFiles, supportFiles).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* There is nothing to review without the document under review, and a run
            that starts without it spends a minute to ask for it. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={!hasFileInputContent(subjectFiles)}
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBCensusNormalizationForm = (props: {
  onSubmit: CopilotCreateWorkspaceHandler;
  error?: CopilotActionError;
  onClose: () => void;
  excelTemplatePicker?: CaraInputTemplatePicker;
}) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('format_for_platform');
  const [message, setMessage] = React.useState('');
  const [censusFiles, setCensusFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [layoutFiles, setLayoutFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [requirementFiles, setRequirementFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [priorCensusFiles, setPriorCensusFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [openDialog, setOpenDialog] = React.useState<CaraTemplateAttachmentKind | null>(null);
  const layoutTemplates = useCaraAttachments();

  // Only the platform run has a destination, so the saved layout, the one-off
  // blank workbook, and every mention of either are gone on the other one. A
  // layout left selected behind a use-case switch would put Cara to work filling
  // a file nobody asked for.
  const isFormatting = useCase === 'format_for_platform';
  const layoutTemplateIds = idsByKind(layoutTemplates.attachments, 'excel-template');

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isFormatting
        ? 'Reformat this census onto the destination layout.'
        : 'Normalize this census and report its exceptions.';

      const messageLines = [
        message.trim() || defaultMessage,
        '',
        // Whether "this census is missing a required field" is sourced or guessed.
        // Without a field list, the prompt reports market-typical gaps to confirm
        // instead of failing the census against a requirement nobody stated.
        `Requirements Supplied: ${hasFileInputContent(requirementFiles) ? 'yes' : 'no'}`,
        `Prior Census Supplied: ${hasFileInputContent(priorCensusFiles) ? 'yes' : 'no'}`,
        `Use Case: ${useCase}`
      ];

      await props.onSubmit(
        messageLines.join('\n'),
        [
          ...renameUploadedFiles(censusFiles.files, '[Census] '),
          ...(isFormatting ? renameUploadedFiles(layoutFiles.files, '[Destination Layout] ') : []),
          ...renameUploadedFiles(requirementFiles.files, '[Field Requirements] '),
          ...renameUploadedFiles(priorCensusFiles.files, '[Prior Census] ')
        ],
        isFormatting && layoutTemplateIds.length > 0
          ? { excelSpreadsheetTemplateIds: layoutTemplateIds }
          : undefined
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose whether this census is going somewhere specific.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'format_for_platform') as UseCase)}
                options={[
                  {
                    value: 'format_for_platform',
                    label: 'Format for a platform',
                    description:
                      'Map the census onto a destination layout: an enrollment platform’s import sheet or a carrier’s RFP template.'
                  },
                  {
                    value: 'clean_and_validate',
                    label: 'Clean and validate',
                    description:
                      'No destination. Normalize the census and get the exception report on what is wrong with it.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>Census</Label>
              <Description>
                {isFormatting
                  ? 'The roster to reformat: a payroll export, an enrollment platform report, or a PDF. Column names do not matter.'
                  : 'The roster to clean: a payroll export, an enrollment platform report, or a PDF. Column names do not matter.'}
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setCensusFiles(change)} />
            </Field>
            {isFormatting && (
              <Field>
                {/* No "(Optional)" tag: this is the input the use case is named for.
                    The submit button still does not gate on it, and the description
                    says what happens without one instead of the tag implying it may
                    be ignored. */}
                <Label>Destination Layout</Label>
                <Description>
                  Pick one of your saved layouts, or upload a blank copy of the file you need
                  filled. Its headers are matched exactly and nothing is added. With neither, the
                  run returns the normalized census and names the layout as the input it needed.
                </Description>
                <div className="flex flex-col gap-2">
                  {layoutTemplates.attachments.length > 0 && (
                    <CaraInputAttachments
                      attachments={layoutTemplates.attachments}
                      onRemove={layoutTemplates.remove}
                    />
                  )}
                  {props.excelTemplatePicker && (
                    <div>
                      <button
                        type="button"
                        aria-label="Choose a saved layout"
                        onClick={() => setOpenDialog('excel-template')}
                        className="group text-neutral-600 dark:text-neutral-400 rounded-full border border-neutral-200 dark:border-neutral-700 flex flex-row items-center gap-1 text-xs py-1 px-1.5 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white transition-all duration-150"
                      >
                        <PlusIcon className="size-3 group-hover:text-neutral-900 dark:group-hover:text-white" />
                        Choose a saved layout
                      </button>
                    </div>
                  )}
                  <FileInput
                    multiple
                    enableExisting
                    onChange={(change) => setLayoutFiles(change)}
                  />
                </div>
              </Field>
            )}
            {/* Both are comparisons the run makes if it can and reports the
                absence of if it cannot: without a field list nothing is failed
                against a requirement nobody stated, and without last year's
                roster there is no changes section. The census alone is a
                complete run. Neither merges into the other or into the census:
                each sets its own flag, and both false branches restrain the run
                rather than merely dropping a section — a layout or a roster
                dropped into a merged bucket would switch on a check whose
                evidence never arrived. The disclosure button already says
                "(optional)" for the group, so the labels inside do not repeat it. */}
            <PresetOptionalUploads
              label="Field requirements and last year’s census"
              files={[...requirementFiles.files, ...priorCensusFiles.files]}
            >
              <Field>
                <Label>Field Requirements</Label>
                <Description>
                  {isFormatting
                    ? 'The platform’s import guide or the carrier’s field list: which fields are required and which code values are accepted. Without it the file matches the layout’s shape but is not checked against the import rules.'
                    : 'The carrier’s field list or the platform’s import guide. With it, an empty required field is a real failure; without it, the run names the fields your census lacks and reports them as items to confirm.'}
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  onChange={(change) => setRequirementFiles(change)}
                />
              </Field>
              <Field>
                <Label>Prior Census</Label>
                <Description>
                  Last renewal’s roster, for the adds, removals, and changes since. Compared only
                  where both files cover the same group, so a division-level file against a
                  company-level one reports no removals. An absence is called a removal, not a
                  termination, unless the prior file says so.
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  onChange={(change) => setPriorCensusFiles(change)}
                />
              </Field>
            </PresetOptionalUploads>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Anything the file does not say: the coverage effective date ages are figured on, the
                plan year, which eligibility classes count, what a numeric tier code means, or which
                lines of coverage to include.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(
                    censusFiles,
                    isFormatting ? layoutFiles : emptyFileInputChange(),
                    requirementFiles,
                    priorCensusFiles
                  ).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
        <TemplatePickerDialogs
          attachments={layoutTemplates.attachments}
          add={layoutTemplates.add}
          remove={layoutTemplates.remove}
          openDialog={openDialog}
          onCloseDialog={() => setOpenDialog(null)}
          excelTemplatePicker={props.excelTemplatePicker}
        />
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* There is nothing to reformat without the census. The destination stays
            optional: with no layout Cara returns the normalized census and names
            the layout as the input that was missing, which beats refusing to run. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={!hasFileInputContent(censusFiles)}
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBQuoteAnalysisForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('normalize_quotes');
  const [message, setMessage] = React.useState('');
  const [censusFiles, setCensusFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [incumbentFiles, setIncumbentFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [contributionFiles, setContributionFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [outputExcel, setOutputExcel] = React.useState(false);
  const options = usePresetOptionCards();

  const isMarketingGrid = useCase === 'build_marketing_grid';
  const isFinalistComparison = useCase === 'build_finalist_comparison';

  // The grid *is* the workbook, so there is no format to choose on that run. The
  // normalization run does not ask either: the workbook it would build has the
  // same tabs as the grid's, so "normalize, with a workbook" is the grid run
  // under another name, and the use-case selector already offers that. Only the
  // finalist run has a real choice — its workbook is the employer's copy, a
  // different file from the internal one. The choice is dropped from the
  // payload where the box is hidden, so a tick left behind by a use-case switch
  // cannot turn a text run into a file.
  const showOutputExcel = isFinalistComparison;
  // Employer-versus-employee cost is the Contribution Analysis preset's job. It
  // earns a column here only where somebody reads the output and decides from it,
  // carried at today's split — the normalization run's deliverable is the
  // reconciliation and the total, and contributions left behind by a use-case
  // switch would put a split into a run that is not about splitting anything.
  const showContributions = !(useCase === 'normalize_quotes');

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isMarketingGrid
        ? 'Build the marketing grid comparing these quoted options.'
        : isFinalistComparison
          ? 'Build the finalist comparison for the employer.'
          : 'Reprice these quoted options on one census and reconcile each against its own quoted total.';

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      const messageLines = [
        message.trim() || defaultMessage,
        '',
        // The names the broker typed, numbered to match the upload prefixes. This
        // is what turns "four rate exhibits and three benefit summaries" into
        // named options whose documents are already paired.
        ...presetOptionNameLines(options.options, 'Carrier Option'),
        `Incumbent Supplied: ${hasFileInputContent(incumbentFiles) ? 'yes' : 'no'}`,
        ...(showContributions
          ? [`Contributions Supplied: ${hasFileInputContent(contributionFiles) ? 'yes' : 'no'}`]
          : []),
        `Output Format: ${isMarketingGrid || (showOutputExcel && outputExcel) ? 'Excel' : 'text'}`,
        `Use Case: ${useCase}`
      ];

      await props.onSubmit(messageLines.join('\n'), [
        ...renameUploadedFiles(censusFiles.files, '[Census] '),
        ...renameUploadedFiles(incumbentFiles.files, '[Incumbent] '),
        ...presetOptionUploads(options.options),
        ...(showContributions
          ? renameUploadedFiles(contributionFiles.files, '[Contributions] ')
          : [])
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose what you need out of this marketing round.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'normalize_quotes') as UseCase)}
                options={[
                  {
                    value: 'normalize_quotes',
                    label: 'Normalize the quotes',
                    description:
                      'Price every option on one census whatever shape its rates arrived in, and reproduce each carrier’s own total before trusting it.'
                  },
                  {
                    value: 'build_marketing_grid',
                    label: 'Build marketing grid',
                    description:
                      'The internal workbook: cost, benefits, funding terms, contingencies, and every difference still unresolved.'
                  },
                  {
                    value: 'build_finalist_comparison',
                    label: 'Compare the finalists',
                    description:
                      'The employer’s copy, carrying only the options whose numbers reproduced.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>Comparison Census</Label>
              <Description>
                One census for the whole comparison, so a cheaper column is a cheaper price rather
                than a smaller group. Include dates of birth and zip for age-banded quotes. CSV or
                Excel.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setCensusFiles(change)} />
            </Field>
            {/* Both describe the arrangement the employer is leaving rather than
                any of the ones being compared, and each adds a column to the
                output instead of deciding whether there is one: without them the
                options are still priced and reconciled against each other. They
                stay two buckets because each sets its own flag and both false
                branches restrain the run — no incumbent means no baseline and no
                option described as a renewal; no contributions means no
                employer-versus-employee split at all. The incumbent is not one
                of the cards below either: the cards are what it is measured
                against, and a card cannot tell the prompt which option is the
                do-nothing default. The disclosure button already says
                "(optional)" for the group, so the labels inside do not repeat it. */}
            <PresetOptionalUploads
              label="What the employer has today"
              files={[
                ...incumbentFiles.files,
                ...(showContributions ? contributionFiles.files : [])
              ]}
            >
              <Field>
                <Label>Current Plan and Incumbent Renewal</Label>
                <Description>
                  The expiring plan and the incumbent’s renewal offer together, told apart by the
                  effective dates printed inside. Every option is then measured against today and
                  against the renewal, which are different claims to beat. Leave empty when there is
                  no coverage or no offer on the table.
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  onChange={(change) => setIncumbentFiles(change)}
                />
              </Field>
              {showContributions && (
                <Field>
                  <Label>Current Contributions</Label>
                  <Description>
                    What the employer contributes today, by tier. Every option then also shows what
                    it does to employees at today’s split. Choosing a different split is the
                    Contribution Analysis preset.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    onChange={(change) => setContributionFiles(change)}
                  />
                </Field>
              )}
            </PresetOptionalUploads>
            <Field>
              <Label>Carrier Options</Label>
              <Description>
                One card per option being shopped, named the way you will name it to the employer;
                the incumbent’s renewal goes above, not in a card. Everything that came back for an
                option belongs in its own card: the rate exhibit with its fee, tax, and stop-loss
                pages, the benefit summary or SBC, and the proposal pages carrying the funding
                arrangement, rate guarantee, and contingencies. The card is what pairs each document
                to its carrier.
              </Description>
              <PresetOptionCards
                cards={options.cards}
                noun="Option"
                namePlaceholder="Aetna Choice POS II $2,000"
                uploadLabel="Documents"
                onAdd={options.add}
                onRemove={options.remove}
                onRename={options.rename}
                onFilesChange={options.setFiles}
              />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Anything the quotes do not say: the effective date you are comparing at, which plans
                map to which, who the finalists are, what the employer has ruled out, and any tier
                definition or child cap you know the rate sheet leaves implicit.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {showOutputExcel && (
              <Field>
                <Label>Output Format</Label>
                <Description>
                  The comparison comes back as text and tables. The workbook is the employer’s copy:
                  cost and benefits side by side, without the internal exception log.
                </Description>
                <CheckboxGroup>
                  <CheckboxField>
                    <Checkbox
                      color="sky"
                      checked={outputExcel}
                      onChange={(v) => setOutputExcel(!!v)}
                    />
                    <Label>Also build an Excel workbook (takes longer)</Label>
                  </CheckboxField>
                </CheckboxGroup>
              </Field>
            )}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles([
                  ...mergeFileInputChanges(
                    censusFiles,
                    incumbentFiles,
                    showContributions ? contributionFiles : emptyFileInputChange()
                  ).files,
                  ...options.files
                ])}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* Both are the run: without the census there is no basis to price on, and
            without at least one option there is nothing to price. The incumbent
            renewal stays optional — plenty of rounds are shopping a group with no
            offer on the table. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={!hasFileInputContent(censusFiles) || options.options.length === 0}
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBBillingAuditForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('reconcile_first_invoice');
  const [message, setMessage] = React.useState('');
  const [invoiceFiles, setInvoiceFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [rosterFiles, setRosterFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [rateFiles, setRateFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [changeFiles, setChangeFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [contractFiles, setContractFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [priorInvoiceFiles, setPriorInvoiceFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [outputExcel, setOutputExcel] = React.useState(false);

  // A first bill has no prior period, so there is no prior invoice to compare
  // against and no what-changed section to produce. Hiding the bucket also keeps
  // a file left behind by a use-case switch from reaching a run that cannot use it.
  const isFirstInvoice = useCase === 'reconcile_first_invoice';

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isFirstInvoice
        ? 'Audit this first invoice against what was enrolled and the rates as bound.'
        : 'Audit this invoice against enrollment and the rates as bound.';

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      const messageLines = [
        message.trim() || defaultMessage,
        '',
        // Whether the run produces money or only population findings. Without
        // bound rates there is no expected charge, so no variance and no credit
        // amount — and the bill's own rates cannot stand in, because those are
        // the numbers under audit.
        `Rates Supplied: ${hasFileInputContent(rateFiles) ? 'yes' : 'no'}`,
        // Whether the proration rule, the retroactive change limit, and the due
        // date are this group's terms or nobody's.
        `Contract Supplied: ${hasFileInputContent(contractFiles) ? 'yes' : 'no'}`,
        // Whether a difference can be attributed at all. An effective date is not
        // a submission date, so without this the run reports what differs and
        // stops short of saying whose error it is.
        `Change Evidence Supplied: ${hasFileInputContent(changeFiles) ? 'yes' : 'no'}`,
        ...(isFirstInvoice
          ? []
          : [`Prior Invoice Supplied: ${hasFileInputContent(priorInvoiceFiles) ? 'yes' : 'no'}`]),
        `Output Format: ${outputExcel ? 'Excel' : 'text'}`,
        `Use Case: ${useCase}`
      ];

      await props.onSubmit(messageLines.join('\n'), [
        ...renameUploadedFiles(invoiceFiles.files, '[Carrier Invoice] '),
        ...renameUploadedFiles(rosterFiles.files, '[Enrollment Roster] '),
        ...renameUploadedFiles(rateFiles.files, '[Bound Rates] '),
        ...renameUploadedFiles(changeFiles.files, '[Submitted Changes] '),
        ...renameUploadedFiles(contractFiles.files, '[Contract and Billing Rules] '),
        ...(isFirstInvoice ? [] : renameUploadedFiles(priorInvoiceFiles.files, '[Prior Invoice] '))
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose which bill this is.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'reconcile_first_invoice') as UseCase)}
                options={[
                  {
                    value: 'reconcile_first_invoice',
                    label: 'Audit the first invoice',
                    description:
                      'The bill after implementation, where a wrong plan code or an unloaded class is cheapest to catch before every later bill inherits it.'
                  },
                  {
                    value: 'reconcile_monthly_invoice',
                    label: 'Audit this period',
                    description:
                      'The ongoing bill: who is billed and no longer covered, who is covered and not billed, and what the retroactive adjustments are settling.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>{isFirstInvoice ? 'First Invoice' : 'Carrier Invoice'}</Label>
              <Description>
                The bill being audited. A list bill with a row per subscriber supports the exception
                register; a summary bill carrying only tier counts is reconciled on counts alone.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setInvoiceFiles(change)} />
            </Field>
            <Field>
              <Label>{isFirstInvoice ? 'Elections and Enrollment' : 'Enrollment Roster'}</Label>
              <Description>
                {isFirstInvoice
                  ? 'What everyone elected at implementation, with plan, tier, and coverage effective dates. Prefer the employer’s or the platform’s copy: a roster from the carrier’s portal only repeats the records the bill was made from.'
                  : 'Who is covered for the period billed, with plan, tier, and coverage dates. Prefer the employer’s or the benefits platform’s export: a roster pulled from the carrier’s portal only repeats the records the bill was made from.'}
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setRosterFiles(change)} />
            </Field>
            <Field>
              <Label>Bound Rates</Label>
              <Description>
                {isFirstInvoice
                  ? 'The sold-case confirmation or final rate sheet, at the rates the carrier agreed to rather than the ones it quoted. Without it the audit names who is billed wrongly but not what the charge should have been, so there is no variance and no credit amount.'
                  : 'The rate sheet in force for this period: the renewal confirmation if rates changed at renewal, otherwise the sold-case confirmation. Without it the audit names who is billed wrongly but not what the charge should have been, so there is no variance and no credit amount.'}
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setRateFiles(change)} />
            </Field>
            {/* Bound rates stay out here, and carry no "(Optional)" tag, because
                they decide what kind of audit this is: with them the run reaches
                a variance and a credit amount, and without them it can only
                establish who is billed wrongly. Submit does not wait on them —
                a population-only audit is a real deliverable — but a tag that
                says "skip this" understates what skipping it costs. The three
                below each sharpen a finding the run already makes — whose error
                it was, which term it broke, how long it has been running — so an
                audit that never opens this is narrower rather than unpriced. */}
            <PresetOptionalUploads
              label={
                isFirstInvoice
                  ? 'Contract terms and what implementation sent'
                  : 'Contract terms, change records, and last period’s bill'
              }
              files={[
                ...changeFiles.files,
                ...contractFiles.files,
                ...(isFirstInvoice ? [] : priorInvoiceFiles.files)
              ]}
            >
              <Field>
                <Label>Submitted Changes and Confirmations</Label>
                <Description>
                  {isFirstInvoice
                    ? 'The transmittal, the EDI report, and the confirmation statements from implementation. An election proves what the employee chose, not that it reached the carrier, so this is what separates a lost file from one that never went.'
                    : 'The adds, terminations, and changes sent for this period, with the dates they went and any confirmation or rejection. Submission dates are what separate a carrier billing error from a change the carrier was never given.'}
                </Description>
                <FileInput multiple enableExisting onChange={(change) => setChangeFiles(change)} />
              </Field>
              <Field>
                <Label>Contract and Billing Rules</Label>
                <Description>
                  The group contract, the carrier’s administrative or billing guide, or a policy
                  exhibit: whichever carries the proration rule, the retroactive change limit, and
                  the premium due date. Without one, nothing is tested against a term, because a
                  market-typical threshold is an assumption rather than this group’s.
                </Description>
                <FileInput
                  multiple
                  enableExisting
                  onChange={(change) => setContractFiles(change)}
                />
              </Field>
              {!isFirstInvoice && (
                <Field>
                  <Label>Prior Invoice</Label>
                  <Description>
                    Last period’s bill, for what changed since, which exceptions have persisted, and
                    whether an adjustment was applied twice. Compared only where both bills cover
                    the same group and account.
                  </Description>
                  <FileInput
                    multiple
                    enableExisting
                    onChange={(change) => setPriorInvoiceFiles(change)}
                  />
                </Field>
              )}
            </PresetOptionalUploads>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Anything the documents do not say: which lines to reconcile, the coverage period if
                the bill does not print one, a change you know went in late, an adjustment you are
                already chasing, or what the bill’s plan and division codes mean.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            <Field>
              <Label>Output Format</Label>
              <Description>
                The audit comes back as text and tables. The workbook adds the exception register as
                a sortable, filterable sheet, which earns its keep once the bill runs to hundreds of
                rows.
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={outputExcel}
                    onChange={(v) => setOutputExcel(!!v)}
                  />
                  <Label>Also build an Excel workbook (takes longer)</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(
                    invoiceFiles,
                    rosterFiles,
                    rateFiles,
                    changeFiles,
                    contractFiles,
                    isFirstInvoice ? emptyFileInputChange() : priorInvoiceFiles
                  ).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* Both are the audit: without the bill there is nothing to check, and
            without the roster there is nothing to check it against — a bill on
            its own can only be described back. Everything else removes a
            specific conclusion and is reported as removed. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={!hasFileInputContent(invoiceFiles) || !hasFileInputContent(rosterFiles)}
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotCommissionProcessingForm = (props: {
  onSubmit: CopilotCreateWorkspaceHandler;
  error?: CopilotActionError;
  onClose: () => void;
  excelTemplatePicker?: CaraInputTemplatePicker;
}) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('convert_statement');
  const [message, setMessage] = React.useState('');
  const [statementFiles, setStatementFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [layoutFiles, setLayoutFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [scheduleFiles, setScheduleFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [bookFiles, setBookFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [priorFiles, setPriorFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [outputExcelChoice, setOutputExcelChoice] = React.useState(false);
  const [outputExcelTouched, setOutputExcelTouched] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState<CaraTemplateAttachmentKind | null>(null);
  const layoutTemplates = useCaraAttachments();

  const isReconciling = useCase === 'reconcile_statement';
  const isAnalyzing = useCase === 'analyze_commission';
  const isConverting = useCase === 'convert_statement';

  // Only conversion is named for the layout — "into one register in your
  // layout" — and the prompt reads a layout as the workbook to fill rather than
  // a source of rows. On the other two runs a layout would only reshape the
  // register while the deliverable is the reconciliation or the movement, so
  // the bucket is hidden there and a file staged before a use-case switch stays
  // out of the payload, the template list, and the workbook default.
  const showLayout = isConverting;

  const layoutTemplateIds = showLayout
    ? idsByKind(layoutTemplates.attachments, 'excel-template')
    : [];

  // A layout is a workbook by definition — the picker exists to be filled — so
  // the checkbox stops being a question once one is chosen. The backend forces
  // the same thing; showing it checked here is so the dialog does not disagree
  // with what the run will do.
  const layoutForcesExcel =
    showLayout && (layoutTemplateIds.length > 0 || hasFileInputContent(layoutFiles));
  // Conversion's whole deliverable is the spreadsheet, so its default is on and
  // the other two default off. The choice sticks once made: a broker who turned
  // the workbook off and then switched use case does not want it back.
  const outputExcel = layoutForcesExcel || (outputExcelTouched ? outputExcelChoice : isConverting);

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isReconciling
        ? 'Check what these statements paid against what was owed.'
        : isAnalyzing
          ? 'Compare this period against the prior one.'
          : 'Convert these statements into one register.';

      const messageLines = [
        message.trim() || defaultMessage,
        '',
        // Each line licenses a claim the statements cannot support on their own,
        // so each is stated rather than inferred from an empty bucket: the
        // schedule is the only sourced rate, the book is the only independent
        // list of what should have been paid on, and the prior period is the
        // whole of the comparison.
        `Schedule Supplied: ${hasFileInputContent(scheduleFiles) ? 'yes' : 'no'}`,
        `Book Of Business Supplied: ${hasFileInputContent(bookFiles) ? 'yes' : 'no'}`,
        `Prior Period Supplied: ${hasFileInputContent(priorFiles) ? 'yes' : 'no'}`,
        `Output Format: ${outputExcel ? 'Excel' : 'text'}`,
        `Use Case: ${useCase}`
      ];

      await props.onSubmit(
        messageLines.join('\n'),
        [
          ...renameUploadedFiles(statementFiles.files, '[Statement] '),
          ...(showLayout ? renameUploadedFiles(layoutFiles.files, '[Destination Layout] ') : []),
          ...renameUploadedFiles(scheduleFiles.files, '[Commission Schedule] '),
          ...renameUploadedFiles(bookFiles.files, '[Book of Business] '),
          ...renameUploadedFiles(priorFiles.files, '[Prior Statement] ')
        ],
        layoutTemplateIds.length > 0
          ? { excelSpreadsheetTemplateIds: layoutTemplateIds }
          : undefined
      );
    } finally {
      setLoading(false);
    }
  };

  const scheduleField = (
    <Field>
      <Label>Commission Schedule</Label>
      <Description>
        The agreed rates by carrier, product, and group: a rate table, a carrier agreement, or the
        schedule exhibit. Without it each line is checked against the basis and rate the statement
        prints, which catches arithmetic errors but not a wrong rate.
      </Description>
      <FileInput multiple enableExisting onChange={(change) => setScheduleFiles(change)} />
    </Field>
  );

  const bookField = (
    <Field>
      <Label>Book of Business</Label>
      <Description>
        A management system export of the accounts that should appear. An account the carrier did
        not pay on is not on the statement, so nothing else makes its absence visible. An export
        naming policy term, billing method, and status beats a plain account list, because a policy
        can be in force and legitimately unpaid.
      </Description>
      <FileInput multiple enableExisting onChange={(change) => setBookFiles(change)} />
    </Field>
  );

  const priorField = (
    <Field>
      <Label>Prior-Period Statements</Label>
      <Description>
        Last period’s statements for the same carriers. Without them there is a single period and no
        movement to decompose into rate, volume, accounts gained and lost, and adjustments.
      </Description>
      <FileInput multiple enableExisting onChange={(change) => setPriorFiles(change)} />
    </Field>
  );

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>What This Run Is For</Label>
              <Description>
                Every run parses the statements and foots each file to its own stated total. This
                chooses what happens after that.
              </Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'convert_statement') as UseCase)}
                options={[
                  {
                    value: 'convert_statement',
                    label: 'Convert to a spreadsheet',
                    description:
                      'Any number of carriers and periods into one register in your layout.'
                  },
                  {
                    value: 'reconcile_statement',
                    label: 'Check what was paid',
                    description:
                      'Test each line against your commission schedule, and name the accounts your book says should have been paid on.'
                  },
                  {
                    value: 'analyze_commission',
                    label: 'Compare to last period',
                    description:
                      'Decompose the movement into rate, volume, accounts gained and lost, and adjustments.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>{isAnalyzing ? 'This Period’s Statements' : 'Commission Statements'}</Label>
              <Description>
                The statements to process: PDF, Excel, or CSV, any number of carriers and periods.
                Each is read in its own layout and footed to the total it prints, so a line dropped
                in parsing surfaces as a failed file rather than as an account nobody paid on.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setStatementFiles(change)} />
            </Field>
            {showLayout && (
              <Field>
                <Label>Destination Layout</Label>
                <Description>
                  A saved layout, or a blank copy of the workbook you need filled. Its headers are
                  matched exactly, and anything the layout has no column for goes on a new tab of
                  its own. With no layout, Cara builds its own register, footing, and exception
                  tabs.
                </Description>
                <div className="flex flex-col gap-2">
                  {layoutTemplates.attachments.length > 0 && (
                    <CaraInputAttachments
                      attachments={layoutTemplates.attachments}
                      onRemove={layoutTemplates.remove}
                    />
                  )}
                  {props.excelTemplatePicker && (
                    <div>
                      <button
                        type="button"
                        aria-label="Choose a saved layout"
                        onClick={() => setOpenDialog('excel-template')}
                        className="group text-neutral-600 dark:text-neutral-400 rounded-full border border-neutral-200 dark:border-neutral-700 flex flex-row items-center gap-1 text-xs py-1 px-1.5 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white transition-all duration-150"
                      >
                        <PlusIcon className="size-3 group-hover:text-neutral-900 dark:group-hover:text-white" />
                        Choose a saved layout
                      </button>
                    </div>
                  )}
                  <FileInput
                    multiple
                    enableExisting
                    onChange={(change) => setLayoutFiles(change)}
                  />
                </div>
              </Field>
            )}
            {/* The inputs a use case is named after are asked for outright; the
                rest collapse. Each one adds a check the statements cannot
                support alone, so an empty bucket removes a section rather than
                degrading one, and none of them is needed to get a register. */}
            {isReconciling && scheduleField}
            {isReconciling && bookField}
            {isAnalyzing && priorField}
            <PresetOptionalUploads
              label={
                isReconciling
                  ? 'Last period’s statements'
                  : isAnalyzing
                    ? 'Commission schedule and book of business'
                    : 'Commission schedule, book of business, and last period'
              }
              files={[
                ...(isReconciling ? [] : scheduleFiles.files),
                ...(isReconciling ? [] : bookFiles.files),
                ...(isAnalyzing ? [] : priorFiles.files)
              ]}
            >
              {!isReconciling && scheduleField}
              {!isReconciling && bookField}
              {!isAnalyzing && priorField}
            </PresetOptionalUploads>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Anything the statements do not say: which agency entity these are addressed to, how
                a carrier’s adjustment codes read, the producer splits to apply, or a period to
                restrict the run to.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            <Field>
              <Label>Output Format</Label>
              <Description>
                {layoutForcesExcel
                  ? 'A destination layout is selected, so the workbook is the deliverable.'
                  : 'The report comes back as text and tables either way. The workbook is where the register itself lives: one row per statement line, sortable and filterable, which matters once a month runs to hundreds of lines.'}
              </Description>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={outputExcel}
                    disabled={layoutForcesExcel}
                    onChange={(v) => {
                      setOutputExcelTouched(true);
                      setOutputExcelChoice(!!v);
                    }}
                  />
                  <Label>Build an Excel workbook (takes longer)</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(
                    statementFiles,
                    showLayout ? layoutFiles : emptyFileInputChange(),
                    scheduleFiles,
                    bookFiles,
                    priorFiles
                  ).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
        <TemplatePickerDialogs
          attachments={layoutTemplates.attachments}
          add={layoutTemplates.add}
          remove={layoutTemplates.remove}
          openDialog={openDialog}
          onCloseDialog={() => setOpenDialog(null)}
          excelTemplatePicker={props.excelTemplatePicker}
        />
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* Reconciling needs something to reconcile against, and only these two
            are it: the schedule prices a line and the book knows an account
            exists. With neither, the run can compare each statement against
            itself and nothing else, which is not what the broker picked. The
            comparison run is not gated the same way — it degrades to a single
            period and says so, where this one would degrade into silence. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={
            !hasFileInputContent(statementFiles) ||
            (isReconciling &&
              !hasFileInputContent(scheduleFiles) &&
              !hasFileInputContent(bookFiles))
          }
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBClaimsReviewForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('quarterly_review');
  const [message, setMessage] = React.useState('');
  const [experienceFiles, setExperienceFiles] =
    React.useState<FileInputChange>(emptyFileInputChange());
  const [claimantFiles, setClaimantFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [stopLossFiles, setStopLossFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  const isPreRenewal = useCase === 'pre_renewal_review';

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessage = isPreRenewal
        ? 'Read this group’s claims experience ahead of the renewal and tell me what to put to underwriting.'
        : 'Read this group’s claims experience and tell me what is driving the cost.';

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      //
      // Only two declarations, and each one gates a section rather than
      // decorating one. The enrollment counts and the funding rates get no
      // line of their own on purpose: they share the experience bucket, so a
      // flag here would report "not supplied" for a document sitting in the
      // upload — and the prompt has to gate those on finding member months and
      // a funding figure in what it actually read, which is a stronger test
      // than a checkbox anyway.
      const messageLines = [
        message.trim() || defaultMessage,
        '',
        // Whether the run reaches a claimant at all. Without this report there
        // is no concentration figure, no piercing test, and nothing for the
        // stop-loss contract to be tested against.
        `Large Claimants Supplied: ${hasFileInputContent(claimantFiles) ? 'yes' : 'no'}`,
        // Whether a specific deductible, an attachment point, and a filing
        // deadline are this group's terms or nobody's. The false branch stops
        // the run guessing one, which is the failure that costs a broker their
        // credibility with the stop-loss carrier.
        `Stop-Loss Supplied: ${hasFileInputContent(stopLossFiles) ? 'yes' : 'no'}`,
        `Use Case: ${useCase}`
      ];

      await props.onSubmit(messageLines.join('\n'), [
        ...renameUploadedFiles(experienceFiles.files, '[Experience] '),
        ...renameUploadedFiles(claimantFiles.files, '[Large Claimants] '),
        ...renameUploadedFiles(stopLossFiles.files, '[Stop-Loss] ')
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Starting Point</Label>
              <Description>Choose what this read is for.</Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'quarterly_review') as UseCase)}
                options={[
                  {
                    value: 'quarterly_review',
                    label: 'Review the experience',
                    description:
                      'The stewardship read: cost per member and where it is going, the medical and pharmacy split, how concentrated the spend is, and the stop-loss position.'
                  },
                  {
                    value: 'pre_renewal_review',
                    label: 'Prepare for renewal',
                    description:
                      'The same arithmetic pointed at the underwriting conversation: what the experience supports, what it does not, and the questions to have answered before the renewal lands.'
                  }
                ]}
              />
            </Field>
            <Field>
              <Label>Claims Experience</Label>
              <Description>
                The carrier’s or TPA’s experience report: paid claims by month, medical and
                pharmacy. Add monthly enrollment counts, pharmacy utilization, and funding or
                premium-equivalent rates here if they came separately. Only self-funded,
                level-funded, and large experience-rated groups have this data; a small fully
                insured group’s premium may not legally depend on its own claims.
              </Description>
              <FileInput
                multiple
                enableExisting
                onChange={(change) => setExperienceFiles(change)}
              />
            </Field>
            {/* Both optional buckets stay on screen rather than behind a
                disclosure, because each one decides what kind of review this is
                rather than sharpening a finding the run already makes. Without
                the claimant report there is no concentration figure and no
                claimant to test a deductible against; without the contract
                there is no deductible. A broker who never sees these boxes
                supplies neither and gets the thinnest version of the review
                without knowing a fuller one existed. The claimant report carries
                no "(Optional)" tag: most experience packages include one, the
                prompt leans on it for the half of the review that finds money,
                and a tag that reads "skip this" understates what skipping it
                costs. The contract keeps its tag because many groups have none
                to give — a fully insured group has no stop-loss — so there the
                tag is true. Neither gates submit. */}
            <Field>
              <Label>Large Claimant Report</Label>
              <Description>
                The claimants above the carrier’s reporting threshold, with diagnosis category and
                paid-to-date. Most experience packages include one; if yours did not, ask the
                carrier or TPA for it. This is the half of the review that finds money:
                concentration, and which claimants pierced the stop-loss deductible. Names are never
                reproduced in the output, and nothing is joined back to the census to put one there.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setClaimantFiles(change)} />
            </Field>
            <Field>
              <Label>Stop-Loss Contract (Optional)</Label>
              <Description>
                The specific deductible, the aggregate attachment, the contract basis, and any
                lasers. Without it nothing is tested against a threshold and none is assumed: a
                reimbursement computed against a market-typical deductible is worse than no figure
                at all.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setStopLossFiles(change)} />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Anything the documents do not say: the funding arrangement if the report does not
                name it, the plan year, a benefit or network change during the period, a known
                one-time event, or an acquisition that moved the headcount.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(
                  mergeFileInputChanges(experienceFiles, claimantFiles, stopLossFiles).files
                )}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* The experience report is the review. A large claimant report on its
            own has no denominator and no period behind it, and a stop-loss
            contract on its own has nothing to test. */}
        <WorkspaceCreateSubmitButton
          loading={loading}
          disabled={!hasFileInputContent(experienceFiles)}
        />
      </DialogActions>
    </Form>
  );
};

export const CopilotEBEnrollmentMaterialsForm = (props: CopilotFormBaseProps) => {
  // DO NOT CHANGE MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
  const [loading, setLoading] = React.useState(false);
  const [useCase, setUseCase] = React.useState<UseCase>('build_enrollment_form');
  const [message, setMessage] = React.useState('');
  const [planFiles, setPlanFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [priorFiles, setPriorFiles] = React.useState<FileInputChange>(emptyFileInputChange());

  // The announcement is the one artifact that precedes the paperwork. A
  // save-the-date needs the enrollment window and what employees have to do,
  // both of which live in the notes; requiring bound rates to write one blocks
  // the run at the only moment it is useful. The prompt keeps plan and cost
  // detail out of an announcement built without documents.
  const isAnnouncement = useCase === 'build_announcement';
  const canSubmit = hasFileInputContent(planFiles) || (isAnnouncement && message.trim().length > 0);

  const onSubmit = async () => {
    setLoading(true);
    try {
      const defaultMessages: Partial<Record<UseCase, string>> = {
        build_enrollment_form:
          'Build the enrollment election form employees will complete for this open enrollment.',
        build_benefits_guide: 'Build the employee benefits guide for this open enrollment.',
        build_announcement: 'Build the open enrollment announcement and FAQ to send to employees.'
      };

      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      //
      // One declaration, because there is only one bucket whose presence opens
      // a section rather than sharpening one. The plan documents get no flag:
      // they are required to submit, so a flag could only ever say yes.
      const messageLines = [
        message.trim() || defaultMessages[useCase] || defaultMessages.build_enrollment_form,
        '',
        // Gates the what-changed section, and the default is deliberately the
        // negative one — the opposite of the sibling presets. This flag opens a
        // section comparing two plan years, and a stale yes would have a run
        // holding one year of material produce a comparison whose only possible
        // source is the current year restated as though it were last year's.
        `Prior Material Supplied: ${hasFileInputContent(priorFiles) ? 'yes' : 'no'}`,
        `Use Case: ${useCase}`
      ];

      // The prefixes assert a plan year and nothing else, because the plan year
      // is the one thing the two buckets differ on: the first holds rates,
      // summaries, and the worksheet alike, so a label naming a document kind
      // would be wrong for most of what lands in it. 044 still checks the year
      // printed inside each file — the label is the broker's claim, not proof.
      await props.onSubmit(messageLines.join('\n'), [
        ...renameUploadedFiles(planFiles.files, '[This Year] '),
        ...renameUploadedFiles(priorFiles.files, '[Last Year] ')
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>What to Build</Label>
              <Description>
                Choose the artifact. All three are built from the same uploads.
              </Description>
              <Combobox
                value={useCase}
                minQueryLength={0}
                maxOptions={10}
                onChange={(v) => setUseCase((v || 'build_enrollment_form') as UseCase)}
                options={[
                  {
                    value: 'build_enrollment_form',
                    label: 'The election form',
                    description:
                      'Two pages an employee signs: the plans and tiers with what each costs per paycheck, an election and a decline box on every line, and the deduction authorization.'
                  },
                  {
                    value: 'build_benefits_guide',
                    label: 'The benefits guide',
                    description:
                      'The booklet: an at-a-glance page, a section per line of coverage with its grid and per-paycheck costs, carrier contacts, and the required notices named rather than drafted.'
                  },
                  {
                    value: 'build_announcement',
                    label: 'The announcement and FAQ',
                    description:
                      'The email: the closing date first, what is changing, what each employee has to do, and the questions the documents can answer.'
                  }
                ]}
              />
            </Field>
            <Field>
              {/* "This Year's" so the two buckets read as a pair the broker can
                  sort by the one thing that separates them. The prompt spends a
                  page on packets that hold this year's rates beside last year's
                  benefit summary — the ordinary case, and one where every figure
                  reconciles while describing a plan no longer on offer — so the
                  label says which year each box is for before a file lands in
                  the wrong one. The "(Optional)" tag on the announcement is
                  honest: that run is written from the notes and carries no
                  plan or cost detail without documents. */}
              <Label>
                {isAnnouncement
                  ? 'This Year’s Rates, Benefit Summaries, and Contributions (Optional)'
                  : 'This Year’s Rates, Benefit Summaries, and Contributions'}
              </Label>
              <Description>
                Everything the material is built from: the bound rates or sold-case confirmation,
                the benefit summaries or schedules of benefits for every line, and the contribution
                worksheet with the employer and employee split. Include the worksheet’s per-paycheck
                column if it has one, since that is where the deduction count comes from. Any figure
                that cannot be traced to one of these stays out of the document.
                {isAnnouncement
                  ? ' An announcement can be written from your notes alone: a save-the-date needs the dates and what employees have to do, not the rates. Without these documents it carries no plan or cost detail.'
                  : ''}
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setPlanFiles(change)} />
            </Field>
            {/* The one optional bucket stays on screen rather than behind a
                disclosure, because it decides whether the material carries the
                section employees read first. The tag is honest: the run builds
                the whole document without it and loses only that section. */}
            <Field>
              <Label>Last Year’s Material (Optional)</Label>
              <Description>
                The prior year’s guide, form, or announcement. It adds the what-changed section
                employees look for first, and it usually states how often employees are paid: a
                fallback when this year’s worksheet has no per-paycheck column and your notes do not
                say. With none of those, the material prints monthly figures rather than guessing a
                payroll calendar.
              </Description>
              <FileInput multiple enableExisting onChange={(change) => setPriorFiles(change)} />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Anything the documents do not say: when enrollment opens and closes, the coverage
                effective date, how often employees are paid, who to contact with questions, the
                waiting period, a line to leave out, or what the employer wants emphasized. Whatever
                is missing comes back as a labeled blank to fill in.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress
                files={extractUploadFiles(mergeFileInputChanges(planFiles, priorFiles).files)}
              />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        {/* Last year's material on its own reproduces last year. Every figure in
            the document has to come from this year's rates and summaries. */}
        <WorkspaceCreateSubmitButton loading={loading} disabled={!canSubmit} />
      </DialogActions>
    </Form>
  );
};

export const CopilotCommercialClassificationForm = (props: CopilotFormBaseProps) => {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [classificationType, setClassificationType] = React.useState<string>('NAICS');

  const onSubmit = async () => {
    setLoading(true);
    try {
      await props.onSubmit(
        // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
        `Classification Type: ${classificationType}\n\n${message}`,
        renameUploadedFiles(inputFiles.files, '[Input Document] '),
        amsContextCreateOption(inputFiles)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Input Documents</Label>
              <Description>
                Upload any documents about the business you want Cara to analyze for classification,
                such as applications, websites, or prior policies.
              </Description>
              <FileInput
                multiple
                enableExisting
                enableAMSSearch
                onChange={(change) => setInputFiles(change)}
              />
            </Field>
            <Field>
              <Label>Classification Type</Label>
              <Description>
                Select the classification system you want to use for this analysis.
              </Description>
              <RadioGroup value={classificationType} onChange={setClassificationType}>
                <RadioField>
                  <Radio color="sky" value="NAICS" />
                  <Label>NAICS</Label>
                </RadioField>
                <RadioField>
                  <Radio color="sky" value="SIC" />
                  <Label>SIC</Label>
                </RadioField>
                <RadioField>
                  <Radio color="sky" value="GL" />
                  <Label>ISO GL Code</Label>
                </RadioField>
              </RadioGroup>
            </Field>
            <Field>
              <Label>Business Description (Optional)</Label>
              <Description>
                Describe the business operations if not already clear from the uploaded documents.
                This helps Cara make a more accurate classification.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
            {props.outputTemplatesField}
          </FieldGroup>
        </Fieldset>
        <AnimatePresence>
          {props.error && (
            <AnimateIn>
              <CopilotSubmitError className="mt-4" error={props.error} />
            </AnimateIn>
          )}
          {loading && (
            <AnimateIn>
              <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
            </AnimateIn>
          )}
        </AnimatePresence>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <WorkspaceCreateSubmitButton loading={loading} />
      </DialogActions>
    </Form>
  );
};
